import { FC, useCallback, useEffect, useMemo } from "react";

import {
  CustomerGender,
  FormCustomFormCustomFormsDescriptionJson,
  FormCustomFormCustomFormsExtraJson,
  FormCustomFormCustomFormsOptionsInner,
  ServiceFormsType,
} from "@/apis/clients";
import Label from "@/components/atoms/inputForms/Label2";
import { FieldError } from "@/components/molecules/service-form/FieldError";
import { EditableFormInput } from "@/components/organisms/service-form/EditableFormInput";
import { MAIL_DELIVERY_TYPES, getFormProps } from "@/models/service/service";
import {
  PREF_OPTIONS,
  PREF_FIELD_NAME,
  INDUSTRY_FIELD_NAME,
  INDUSTRY_OTHER_ID,
} from "@/utils/const";
import {
  FieldValueChangeHandler,
  FormOptions,
  ServiceFormCustomerData,
  ServiceFormCustomerInputs,
  ServiceFormErrors,
} from "@/states/service-form/types";
import { format, parseValidDate } from "@/utils/date";
import { getCustomFieldKey } from "@/states/service-form/operations";
import { checkIsCustomDescription, checkIsCustomField } from "@/utils/common";
import { TextField } from "@/components/atoms/inputForms/TextField";
import clsx from "clsx";
import { contentJsonAsHtml } from "@/utils/draft";
import { IFileUpload } from "@/components/organisms/service-form/CustomFormUploadFile";

export interface FormProp {
  id: string;
  label: string;
  type: string | number;
  fieldName?: keyof ServiceFormCustomerInputs;
  placeholder: string;
  options?: FormCustomFormCustomFormsOptionsInner[];
  disabled?: boolean;
  required?: boolean;
  data?: unknown;
  defaultValue?: string | number;
  fields?: FormProp[];
  description?: string;
  descriptionJson?: FormCustomFormCustomFormsDescriptionJson;
  extraJson?: FormCustomFormCustomFormsExtraJson;
  autoComplete?: string;
}

export interface FormItemsProps {
  formData: ServiceFormCustomerData[];
  inputs: Partial<ServiceFormCustomerInputs>;
  errors: ServiceFormErrors;
  onFieldValueChange: FieldValueChangeHandler;
  options: FormOptions;
  isBorder?: boolean;
}

interface Props {
  props: FormItemsProps;
}

interface InitCustomerForm {
  firstName?: string;
  lastName?: string;
  email?: string;
}

/* TODO FormRowsRendererの内容がuser側とかなり乖離しているため修正の必要あり？ */
/* TODO TextFieldコンポーネントのスタイルがuser側と異なるため一時的にテンプレート側にて調整(mt-1を追加)しています。 */
export const FormRowsRenderer: FC<Props> = ({ props }) => {
  const { formData, inputs, errors, onFieldValueChange, options } = props;

  const iOptions = (options.industries ?? []).map((i) => ({
    id: i.value as string,
    label: i.text,
    value: `${i.value}`, // 住所検索のAPIがprefCodeをstringで返す為そちらに合わせてこのタイミングでキャスト
  }));
  const industryOptions = [
    {
      id: "",
      label: "未選択",
      value: "",
    },
  ].concat(...iOptions);
  const birthday: Date | undefined = useMemo(
    () => parseValidDate(inputs.birthday),
    [inputs.birthday],
  );

  const onBirthdayChange = useCallback(
    (date: Date | undefined): void => {
      onFieldValueChange("birthday", date ? format(date, "yyyy/MM/dd") : "");
    },
    [onFieldValueChange],
  );
  const hasNameField = formData.some(
    (item) => item.type === ServiceFormsType.NAME
  );

  const hasEmailField = formData.some(
    (item) => item.type === ServiceFormsType.MAIL_ADDRESS
  );

  useEffect(() => {
    const customerFormLocal = localStorage.getItem('customer_form');
    if (!customerFormLocal) return;

    const customerForm = JSON.parse(customerFormLocal) as InitCustomerForm;

    // Only fill from localStorage if form has the corresponding field
    if (hasNameField && customerForm.firstName) onFieldValueChange('firstName', customerForm.firstName || '');
    if (hasNameField && customerForm.lastName) onFieldValueChange('lastName', customerForm.lastName);

    if (hasEmailField && customerForm.email) onFieldValueChange('email', customerForm.email);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNameField, hasEmailField]);

  const onChangeCheckbox = useCallback(
    () =>
      (props: {
        serviceFormCustomerData: ServiceFormCustomerData;
        item: FormCustomFormCustomFormsOptionsInner;
        isCheckboxField?: boolean;
        key?: string;
        text?: string;
      }) => {
        const { serviceFormCustomerData, item, isCheckboxField, key, text } =
          props;
        const {
          type: formType,
          customForm,
          serviceFormId,
        } = serviceFormCustomerData;

        const isCustomField = checkIsCustomField(formType);

        if (isCustomField) {
          const formKey = getCustomFieldKey(
            formType,
            serviceFormId,
            customForm.serviceCustomFormId,
          );
          const customFormValue = inputs[`${formKey}`] as string | number | boolean | FormCustomFormCustomFormsOptionsInner[] | undefined;

          if (
            customFormValue &&
            isCheckboxField &&
            typeof customFormValue == "object"
            // customFormValue?.length
          ) {
            const exitedItemIndex = customFormValue?.findIndex(
              (select) => select.id === item.id,
            );

            if (text) {
              let newValue = [...(customFormValue || [])];
              if (exitedItemIndex !== -1) {
                newValue[exitedItemIndex] = {
                  ...newValue[exitedItemIndex],
                  otherLabel: text,
                };
              } else {
                newValue = [...newValue, { ...item, otherLabel: text }];
              }
              onFieldValueChange(formKey, newValue);
            } else {
              const newValue =
                exitedItemIndex !== -1
                  ? customFormValue?.filter(
                    (select) => select.id !== item.id,
                    [],
                  )
                  : [...(customFormValue || []), item];

              onFieldValueChange(formKey, newValue);
            }
          } else {
            const value = [{ ...item, otherLabel: text || undefined }];
            onFieldValueChange(formKey, value);
          }
        } else {
          key && onFieldValueChange(key, [item]);
        }
      },
    [inputs],
  );

  const onChangeDropdown = useCallback(
    () =>
      (props: {
        serviceFormCustomerData: ServiceFormCustomerData;
        item: FormCustomFormCustomFormsOptionsInner;
        key?: string;
        text?: string;
      }) => {
        const { serviceFormCustomerData, item, key, text } = props;
        const {
          type: formType,
          customForm,
          serviceFormId,
        } = serviceFormCustomerData;

        const isCustomField = checkIsCustomField(formType);

        if (isCustomField) {
          const formKey = getCustomFieldKey(
            formType,
            serviceFormId,
            customForm.serviceCustomFormId,
          );
          let newValue = item;
          if (text) {
            newValue = { ...item, otherLabel: text };
          }
          onFieldValueChange(formKey, [newValue]);
        } else if (key) {
          onFieldValueChange(key, item.id);
        }
      },
    [inputs],
  );

  const Address2FormTypes: ServiceFormsType[] = [
    ServiceFormsType.PREFECTURE,
    ServiceFormsType.ADDRESS1,
  ];
  const FormItems = useMemo(
    () =>
      formData.map((formItem, formIndex) => {
        const { type: formType, customForm, serviceFormId } = formItem;
        const isRequired = !Address2FormTypes.includes(formType)
          ? customForm?.isRequired
          : false;
        const disabled: boolean =
          formType === ServiceFormsType.MAIL_ADDRESS ||
          formType === ServiceFormsType.MAIL_DELIVERY ||
          formType === ServiceFormsType.NAME;

        const isCustomField = checkIsCustomField(formType);
        const isCustomDescription = checkIsCustomDescription(formType);

        // タイプによって描画に必要なデータを取得
        const formProps: FormProp[] = getFormProps(formType);
        const formCustomFieldKey = isCustomField
          ? formType === ServiceFormsType.UPLOAD
            ? 'files' : getCustomFieldKey(
              formType,
              serviceFormId,
              customForm.serviceCustomFormId,
            )
          : undefined;

        const formItems = () =>
          formProps.map((item) => {
            let fields = formProps;
            if (item.fields) {
              fields = item.fields;
            }
            const fieldItems = fields.map((prop) => {
              const isFieldRequired = isRequired && !item.fields;
              const label = isCustomField ? customForm.label : prop.label;
              let options = isCustomField ? customForm.options : prop.options;
              if (prop.fieldName === INDUSTRY_FIELD_NAME) {
                options = industryOptions.map((option) => ({
                  ...option,
                  value: option.value ? Number(option.value) : "",
                  id: option.id,
                }));
              }
              if (prop.fieldName === PREF_FIELD_NAME) {
                options = PREF_OPTIONS.map((pre) => {
                  return {
                    id: pre.value,
                    label: pre.text,
                    value: pre.value,
                  };
                });
              }
              const fieldtype = isCustomField ? customForm.type : prop.type;
              const description = isCustomField
                ? customForm.description
                : prop.description;
              const descriptionJson = isCustomField || isCustomDescription ? customForm.descriptionJson : prop.descriptionJson;
              const extraJson = isCustomField ? customForm.extraJson : prop.extraJson;

              let value = undefined;
              if (isCustomField && formCustomFieldKey) {
                value = inputs[`${formCustomFieldKey}`];
              } else {
                value =
                  inputs && prop.fieldName
                    ? inputs[`${prop.fieldName}`]
                    : undefined;
              }
              const fieldInputName =
                formCustomFieldKey || prop.fieldName?.toString();

              const fieldClassName =
                prop.id === "prefecture" ? "!w-[302px]" : "";
              const uploadConfig = {
                maximumFile: formItem.customForm.maximumFile,
                fileType: formItem.customForm.fileType
              };
              return (
                <div
                  key={prop.id}
                  className={
                    formProps.length > 1
                      ? "basis-1/" + String(formProps.length)
                      : "w-full"
                  }
                >
                  <EditableFormInput
                    id={`${String(prop.id)}-${formIndex}`}
                    inputOrder={formIndex}
                    label={label}
                    type={fieldtype}
                    placeholder={prop.placeholder}
                    required={isFieldRequired}
                    options={options as FormCustomFormCustomFormsOptionsInner[]}
                    disabled={disabled}
                    value={value}
                    birthday={birthday}
                    defaultValue={prop.defaultValue}
                    autoComplete={prop.autoComplete}
                    description={description}
                    descriptionJson={descriptionJson}
                    extraJson={extraJson}
                    errors={errors}
                    inputs={inputs}
                    fieldClassName={fieldClassName}
                    formCustomFieldKey={formCustomFieldKey}
                    uploadConfig={uploadConfig}
                    onTextValueChange={(value, name) => {
                      if (name) {
                        onFieldValueChange(name, value);
                        return;
                      }
                      if (fieldInputName) {
                        onFieldValueChange(fieldInputName, value || "");
                        return;
                      }
                    }}
                    onBirthdayChange={onBirthdayChange}
                    onChangeRadio={(item, text) => {
                      if (prop.fieldName === "mailMagazine") {
                        onFieldValueChange(
                          "mailMagazine",
                          item.value === MAIL_DELIVERY_TYPES.allow.value,
                        );
                        return;
                      }
                      if (prop.fieldName === "gender") {
                        onFieldValueChange(
                          "gender",
                          item.value as CustomerGender,
                        );
                        return;
                      }
                      onChangeCheckbox()({
                        serviceFormCustomerData: formItem,
                        item,
                        text,
                      });
                    }}
                    onChangeCheckbox={(item, text) =>
                      onChangeCheckbox()({
                        serviceFormCustomerData: formItem,
                        item,
                        isCheckboxField: true,
                        text: text,
                      })
                    }
                    onChangeDropdown={(item, text) =>
                      onChangeDropdown()({
                        serviceFormCustomerData: formItem,
                        item,
                        key: prop.fieldName?.toString(),
                        text,
                      })
                    }
                    onChangeFile={(files: IFileUpload[]) => {
                      onFieldValueChange("files", files);
                    }}
                  />
                  {isCustomField && errors && formCustomFieldKey && (
                    <FieldError errors={errors} name={formCustomFieldKey} />
                  )}
                  {errors && prop.fieldName && formType !== ServiceFormsType.UPLOAD && (
                    <FieldError errors={errors} name={prop.fieldName} />
                  )}
                  {prop.fieldName === INDUSTRY_FIELD_NAME &&
                    inputs.industryId === INDUSTRY_OTHER_ID && (
                      <>
                        <TextField
                          id={`industry-other-${formIndex}`}
                          name="industry-other"
                          type="text"
                          placeholder="その他の業種"
                          value={inputs.industryOther || ""}
                          onChange={(ev) =>
                            onFieldValueChange("industryOther", ev.target.value)
                          }
                          className="mt-1"
                        />
                        <FieldError errors={errors} name="industryOther" />
                      </>
                    )}
                </div>
              );
            });

            return (
              <div className="w-full" key={item.id}>
                {item.fields && item.label && (
                  <Label
                    label={item.label}
                    isRequired={
                      isRequired &&
                      item.label !== undefined &&
                      item.label !== ""
                    }
                    description={contentJsonAsHtml(item.descriptionJson)}
                    htmlFor={`${String(item.fields[0].id)}-${formIndex}`}
                  />
                )}
                <div
                  className={clsx("space-y-0 flex gap-4 w-full items-start", {
                    "flex flex-col": formType === ServiceFormsType.ADDRESS2,
                  })}
                >
                  {fieldItems}
                </div>
              </div>
            );
          });

        return (
          <div key={formIndex} className="space-y-4 w-full">
            <div className="flex items-center space-x-4 w-full cursor-auto">
              <div
                className={`space-y-2 md:space-y-0 md:flex md:gap-4 md:items-end w-full`}
              >
                {formItems()}
              </div>
            </div>
          </div>
        );
      }),
    [inputs, onChangeCheckbox],
  );

  return <>{FormItems}</>;
};
